import { useState } from 'react';
import { AlertCircle, DollarSign } from 'lucide-react';
import api from '../utils/axios.ts';
import { toast } from 'react-hot-toast';

const LoanWriteOffForm = ({ loan, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    write_off_type: 'full',
    write_off_amount: loan.loan_balance,
    reason: '',
    notes: '',
    write_off_date: new Date().toISOString().split('T')[0],
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.write_off_type === 'full' && parseFloat(formData.write_off_amount) !== parseFloat(loan.loan_balance)) {
      toast.error('Full write-off amount must equal the outstanding balance');
      return;
    }

    if (parseFloat(formData.write_off_amount) > parseFloat(loan.loan_balance)) {
      toast.error('Write-off amount cannot exceed outstanding balance');
      return;
    }

    setLoading(true);
    try {
      await api.post('/loan-write-offs', {
        loan_id: loan.id,
        ...formData,
      });
      
      toast.success('Write-off request created successfully');
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create write-off request');
    } finally {
      setLoading(false);
    }
  };

  const handleTypeChange = (type) => {
    setFormData({
      ...formData,
      write_off_type: type,
      write_off_amount: type === 'full' ? loan.loan_balance : '',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  return (
    <div className="bg-white rounded-lg p-6">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Write-Off Loan</h3>
        <p className="text-sm text-gray-600">
          Create a write-off request for this loan. This action requires approval.
        </p>
      </div>

      {/* Loan Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 mr-3" />
          <div className="flex-1">
            <p className="text-sm font-medium text-blue-900">Loan Information</p>
            <div className="mt-2 space-y-1 text-sm text-blue-800">
              <p>Loan Number: <span className="font-semibold">{loan.loan_no}</span></p>
              <p>Member: <span className="font-semibold">{loan.member?.name}</span></p>
              <p>Outstanding Balance: <span className="font-semibold">{formatCurrency(loan.loan_balance)}</span></p>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Write-off Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Write-off Type *
          </label>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => handleTypeChange('full')}
              className={`p-4 border-2 rounded-lg text-left transition-colors ${
                formData.write_off_type === 'full'
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <div className="font-semibold text-gray-900">Full Write-Off</div>
              <div className="text-sm text-gray-600 mt-1">
                Write off entire outstanding balance
              </div>
            </button>
            <button
              type="button"
              onClick={() => handleTypeChange('partial')}
              className={`p-4 border-2 rounded-lg text-left transition-colors ${
                formData.write_off_type === 'partial'
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <div className="font-semibold text-gray-900">Partial Write-Off</div>
              <div className="text-sm text-gray-600 mt-1">
                Write off a portion of the balance
              </div>
            </button>
          </div>
        </div>

        {/* Write-off Amount */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Write-off Amount *
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <DollarSign className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="number"
              value={formData.write_off_amount}
              onChange={(e) => setFormData({ ...formData, write_off_amount: e.target.value })}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="0.00"
              step="0.01"
              min="0"
              max={loan.loan_balance}
              required
              disabled={formData.write_off_type === 'full'}
            />
          </div>
          <p className="mt-1 text-sm text-gray-500">
            Maximum: {formatCurrency(loan.loan_balance)}
          </p>
        </div>

        {/* Reason */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Reason for Write-off *
          </label>
          <select
            value={formData.reason}
            onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
            className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          >
            <option value="">Select reason</option>
            <option value="Member deceased">Member deceased</option>
            <option value="Member bankrupt">Member bankrupt</option>
            <option value="Business closed">Business closed</option>
            <option value="Unrecoverable debt">Unrecoverable debt</option>
            <option value="Legal settlement">Legal settlement</option>
            <option value="Other">Other</option>
          </select>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Additional Notes
          </label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows="4"
            placeholder="Provide any additional details about this write-off..."
          />
        </div>

        {/* Write-off Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Write-off Date *
          </label>
          <input
            type="date"
            value={formData.write_off_date}
            onChange={(e) => setFormData({ ...formData, write_off_date: e.target.value })}
            className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>

        {/* Warning */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 mr-3" />
            <div className="flex-1">
              <p className="text-sm font-medium text-yellow-900">Important</p>
              <p className="text-sm text-yellow-800 mt-1">
                This write-off request will be sent for approval. Once approved, the loan balance will be adjusted and the loan status will be updated accordingly.
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex space-x-3 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Write-off Request'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LoanWriteOffForm;
